import _defineProperty from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _slicedToArray from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _objectWithoutProperties from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["view"],
    _excluded2 = ["state", "composite", "focusOnMove"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useRef, useMemo, useState, useCallback, useEffect } from 'react';
import { flatten2DArray, reverseArray } from 'ariakit-utils/array';
import { getActiveElement, isTextField } from 'ariakit-utils/dom';
import { fireBlurEvent, fireFocusEvent, isSelfTarget, fireKeyboardEvent } from 'ariakit-utils/events';
import { hasFocus, focusIntoView } from 'ariakit-utils/focus';
import { useLiveRef, useSafeLayoutEffect, useEvent, useForkRef } from 'ariakit-utils/hooks';
import { queueMicrotask } from 'ariakit-utils/misc';
import { useStoreProvider } from 'ariakit-utils/store';
import { createHook, createComponent, createElement } from 'ariakit-utils/system';
import { useFocusable } from '../focusable/focusable.js';
import { f as findEnabledItemById, s as silentlyFocused, i as isItem, C as CompositeContext, a as findFirstEnabledItem, g as groupItemsByRows } from '../__utils-7009759c.js';

function isGrid(items) {
  return items.some(function (item) {
    return !!item.rowId;
  });
}

function isPrintableKey(event) {
  return event.key.length === 1 && !event.ctrlKey && !event.metaKey;
}

function canProxyKeyboardEvent(event, state) {
  if (!isSelfTarget(event)) return false;
  var target = event.target;
  if (!target) return true;

  if (isTextField(target)) {
    // Printable characters shouldn't perform actions on the composite items if
    // the composite widget is a combobox.
    if (isPrintableKey(event)) return false;
    var grid = isGrid(state.items);
    var focusingInputOnly = state.activeId === null; // Pressing Home or End keys on the text field should only be allowed when
    // the widget has rows and the input is not the only element with focus.
    // That is, the aria-activedescendant has no value.

    var allowHorizontalNavigationOnItems = grid && !focusingInputOnly;
    var isHomeOrEnd = event.key === "Home" || event.key === "End"; // If there are no rows or the input is the only focused element, then we
    // should stop the event propagation so no action is performed on the
    // composite items, but only on the input, like moving the caret/selection.

    if (!allowHorizontalNavigationOnItems && isHomeOrEnd) return false;
  }

  return !event.isPropagationStopped();
}

function useKeyboardEventProxy(state, activeItem, onKeyboardEvent, previousElementRef) {
  return useEvent(function (event) {
    onKeyboardEvent == null ? void 0 : onKeyboardEvent(event);
    if (event.defaultPrevented) return;
    if (!canProxyKeyboardEvent(event, state)) return;
    var activeElement = activeItem == null ? void 0 : activeItem.ref.current;
    if (!activeElement) return;

    var view = event.view,
        eventInit = _objectWithoutProperties(event, _excluded);

    var previousElement = previousElementRef == null ? void 0 : previousElementRef.current; // If the active item element is not the previous element, this means that
    // it hasn't been focused (for example, when using composite hover). So we
    // focus on it before firing the keyboard event.

    if (activeElement !== previousElement) {
      activeElement.focus();
    }

    if (!fireKeyboardEvent(activeElement, event.type, eventInit)) {
      event.preventDefault();
    } // The event will be triggered on the composite item and then propagated up
    // to this composite element again, so we can pretend that it wasn't called
    // on this component in the first place.


    if (event.currentTarget.contains(activeElement)) {
      event.stopPropagation();
    }
  });
}

function findFirstEnabledItemInTheLastRow(items) {
  return findFirstEnabledItem(flatten2DArray(reverseArray(groupItemsByRows(items))));
}

function useScheduleFocus(activeItem) {
  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      scheduled = _useState2[0],
      setScheduled = _useState2[1];

  var schedule = useCallback(function () {
    return setScheduled(true);
  }, []);
  useEffect(function () {
    var activeElement = activeItem == null ? void 0 : activeItem.ref.current;

    if (scheduled && activeElement) {
      setScheduled(false);
      focusIntoView(activeElement);
    }
  }, [activeItem, scheduled]);
  return schedule;
}
/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render a composite widget.
 * @see https://ariakit.org/components/composite
 * @example
 * ```jsx
 * const state = useCompositeState();
 * const props = useComposite({ state });
 * <Role {...props}>
 *   <CompositeItem>Item 1</CompositeItem>
 *   <CompositeItem>Item 2</CompositeItem>
 * </Role>
 * ```
 */


var useComposite = createHook(function (_ref) {
  var state = _ref.state,
      _ref$composite = _ref.composite,
      composite = _ref$composite === void 0 ? true : _ref$composite,
      _ref$focusOnMove = _ref.focusOnMove,
      focusOnMove = _ref$focusOnMove === void 0 ? composite : _ref$focusOnMove,
      props = _objectWithoutProperties(_ref, _excluded2);

  var ref = useRef(null);
  var virtualFocus = composite && state.virtualFocus;
  var activeItem = useMemo(function () {
    return findEnabledItemById(state.items, state.activeId);
  }, [state.items, state.activeId]);
  var activeItemRef = useLiveRef(activeItem);
  var previousElementRef = useRef(null);
  var isSelfActive = state.activeId === null;
  var isSelfAciveRef = useLiveRef(isSelfActive);
  var scheduleFocus = useScheduleFocus(activeItem); // Focus on the active item element.

  useSafeLayoutEffect(function () {
    var _activeItemRef$curren;

    if (!composite) return;
    if (!focusOnMove) return;
    if (!state.moves) return;
    var itemElement = (_activeItemRef$curren = activeItemRef.current) == null ? void 0 : _activeItemRef$curren.ref.current;
    if (!itemElement) return; // We're scheduling the focus on the next tick to avoid the `onFocus`
    // event on each item to be triggered before the state changes can
    // propagate to them.

    scheduleFocus();
  }, [composite, focusOnMove, state.moves]); // If composite.move(null) has been called, the composite container (this
  // element) should receive focus.

  useSafeLayoutEffect(function () {
    if (!composite) return;
    if (!state.moves) return;
    if (!isSelfAciveRef.current) return;
    var element = ref.current;
    if (!element) return;
    var previousElement = previousElementRef.current; // We have to clean up the previous element ref so an additional blur
    // event is not fired on it, for example, when looping through items while
    // includesBaseElement is true.

    previousElementRef.current = null;

    if (previousElement) {
      // We fire a blur event on the previous active item before moving focus
      // to the composite element so the events are dispatched in the right
      // order (blur, then focus).
      fireBlurEvent(previousElement, {
        relatedTarget: element
      });
    } // If the composite element is already focused, we still need to fire a
    // focus event on it so consumer props like onFocus are called.


    if (hasFocus(element)) {
      fireFocusEvent(element, {
        relatedTarget: previousElement
      });
    } else {
      element.focus();
    }
  }, [composite, state.moves]); // At this point, if the activeId has changed and we still have a
  // previousElement, this means that the previousElement hasn't been blurred,
  // so we do it here. This will be the scenario when moving through items, in
  // which case the onFocusCapture below event will stop propgation.

  useSafeLayoutEffect(function () {
    var _activeItemRef$curren2;

    if (!virtualFocus) return;
    if (!composite) return;
    var previousElement = previousElementRef.current;
    previousElementRef.current = null;
    if (!previousElement) return;
    var activeElement = (_activeItemRef$curren2 = activeItemRef.current) == null ? void 0 : _activeItemRef$curren2.ref.current;
    var relatedTarget = activeElement || getActiveElement(previousElement);
    fireBlurEvent(previousElement, {
      relatedTarget: relatedTarget
    });
  }, [virtualFocus, composite, state.activeId]);
  var onKeyDownCapture = useKeyboardEventProxy(state, activeItem, props.onKeyDownCapture, previousElementRef);
  var onKeyUpCapture = useKeyboardEventProxy(state, activeItem, props.onKeyUpCapture, previousElementRef);
  var onFocusCaptureProp = props.onFocusCapture;
  var onFocusCapture = useEvent(function (event) {
    onFocusCaptureProp == null ? void 0 : onFocusCaptureProp(event);
    if (event.defaultPrevented) return;
    if (!virtualFocus) return;
    var previousActiveElement = event.relatedTarget;
    var isSilentlyFocused = silentlyFocused(event.currentTarget);

    if (isSelfTarget(event) && isSilentlyFocused) {
      // Composite has been focused as a result of an item receiving focus.
      // The composite item will move focus back to the composite container.
      // In this case, we don't want to propagate this additional event nor
      // call the onFocus handler passed to <Composite onFocus={...} />.
      event.stopPropagation(); // We keep track of the previous active item element so we can manually
      // fire a blur event on it later when the focus is moved to another item
      // on the onBlurCapture event below.

      previousElementRef.current = previousActiveElement;
    }
  });
  var onFocusProp = props.onFocus;
  var onFocus = useEvent(function (event) {
    onFocusProp == null ? void 0 : onFocusProp(event);
    if (event.defaultPrevented) return;
    if (!composite) return;

    if (virtualFocus) {
      // This means that the composite element has been focused while the
      // composite item has not. For example, by clicking on the composite
      // element without touching any item, or by tabbing into the composite
      // element. In this case, we want to trigger focus on the item, just
      // like it would happen with roving tabindex. When it receives focus,
      // the composite item will move focus back to the composite element.
      if (isSelfTarget(event) && !isItem(state.items, event.relatedTarget)) {
        // By queueing a microtask, we ensure the scheduleFocus effect will be
        // triggered after all the other effects that might have changed the
        // active item. This also accounts for when the composite container is
        // focused right after it gets mounted (for example, in a dialog), in
        // which case state.items will not be populated yet.
        queueMicrotask(scheduleFocus);
      }
    } else if (isSelfTarget(event)) {
      // When the roving tabindex composite gets intentionally focused (for
      // example, by clicking directly on it, and not on an item), we make
      // sure to set the activeId to null (which means the composite element
      // itself has focus).
      state.setActiveId(null);
    }
  });
  var onBlurCaptureProp = props.onBlurCapture;
  var onBlurCapture = useEvent(function (event) {
    onBlurCaptureProp == null ? void 0 : onBlurCaptureProp(event);
    if (event.defaultPrevented) return;
    if (!virtualFocus) return; // When virtualFocus is set to true, we move focus from the composite
    // container (this element) to the composite item that is being selected.
    // Then we move focus back to the composite container. This is so we can
    // provide the same API as the roving tabindex method, which means people
    // can attach onFocus/onBlur handlers on the CompositeItem component
    // regardless of whether virtualFocus is set to true or false. This
    // sequence of blurring and focusing on items and on the composite element
    // may be confusing, so we ignore intermediate focus and blur events by
    // stopping their propagation.

    var activeElement = (activeItem == null ? void 0 : activeItem.ref.current) || null;
    var nextActiveElement = event.relatedTarget;
    var nextActiveElementIsItem = isItem(state.items, nextActiveElement);
    var previousElement = previousElementRef.current;
    previousElementRef.current = null; // This is an intermediate blur event: blurring the composite container
    // to focus on an item (nextActiveElement).

    if (isSelfTarget(event) && nextActiveElementIsItem) {
      // The next active element will be the same as the active item in the
      // state in these two scenarios:
      //   - Moving focus with keyboard: the state is updated before the blur
      //     event is triggered, so here the active item is already pointing
      //     to the next active element.
      //   - Clicking on the active item with a pointer: this will trigger
      //     blur on the composite element and then the next active element
      //     will be the same as the active item. Clicking on an item other
      //     than the active one doesn't end up here as the activeItem state
      //     will be updated only after that.
      if (nextActiveElement === activeElement) {
        // If there's a previous active item and it's not a click action, then
        // we fire a blur event on it so it will work just like if it had DOM
        // focus before (like when using roving tabindex).
        if (previousElement && previousElement !== nextActiveElement) {
          fireBlurEvent(previousElement, event);
        }
      } // This will be true when the next active element is not the active
      // element, but there's an active item. This will only happen when
      // clicking with a pointer on a different item, when there's already an
      // item selected, in which case activeElement is the item that is
      // getting blurred, and nextActiveElement is the item that is being
      // clicked.
      else if (activeElement) {
        fireBlurEvent(activeElement, event);
      } // We want to ignore intermediate blur events, so we stop the
      // propagation of this event.


      event.stopPropagation();
    } else {
      var targetIsItem = isItem(state.items, event.target); // If target is not a composite item, it may be the composite element
      // itself (isSelfTarget) or a tabbable element inside the composite
      // element. This may be triggered by clicking outside of the composite
      // element or by tabbing out of it. In either cases, we want to fire a
      // blur event on the active item.

      if (!targetIsItem && activeElement) {
        fireBlurEvent(activeElement, event);
      }
    }
  });
  var onKeyDownProp = props.onKeyDown;
  var onKeyDown = useEvent(function (event) {
    onKeyDownProp == null ? void 0 : onKeyDownProp(event);
    if (event.defaultPrevented) return;
    if (!isSelfTarget(event)) return;
    if (activeItemRef.current) return;
    var isVertical = state.orientation !== "horizontal";
    var isHorizontal = state.orientation !== "vertical";
    var grid = isGrid(state.items);

    var up = function up() {
      if (grid) {
        var item = state.items && findFirstEnabledItemInTheLastRow(state.items);
        return item == null ? void 0 : item.id;
      }

      return state.last();
    };

    var keyMap = {
      ArrowUp: (grid || isVertical) && up,
      ArrowRight: (grid || isHorizontal) && state.first,
      ArrowDown: (grid || isVertical) && state.first,
      ArrowLeft: (grid || isHorizontal) && state.last,
      Home: state.first,
      End: state.last,
      PageUp: state.first,
      PageDown: state.last
    };
    var action = keyMap[event.key];

    if (action) {
      var id = action();

      if (id !== undefined) {
        event.preventDefault();
        state.move(id);
      }
    }
  });
  props = useStoreProvider(_objectSpread({
    state: state
  }, props), CompositeContext);
  var activeId = (activeItem == null ? void 0 : activeItem.id) || undefined;
  props = _objectSpread(_objectSpread({
    "aria-activedescendant": virtualFocus ? activeId : undefined
  }, props), {}, {
    ref: useForkRef(ref, composite ? state.baseRef : undefined, props.ref),
    onKeyDownCapture: onKeyDownCapture,
    onKeyUpCapture: onKeyUpCapture,
    onFocusCapture: onFocusCapture,
    onFocus: onFocus,
    onBlurCapture: onBlurCapture,
    onKeyDown: onKeyDown
  });
  var focusable = composite && (virtualFocus || state.activeId === null);
  props = useFocusable(_objectSpread({
    focusable: focusable
  }, props));
  return props;
});
/**
 * A component that renders a composite widget.
 * @see https://ariakit.org/components/composite
 * @example
 * ```jsx
 * const composite = useCompositeState();
 * <Composite state={composite}>
 *   <CompositeItem>Item 1</CompositeItem>
 *   <CompositeItem>Item 2</CompositeItem>
 * </Composite>
 * ```
 */

var Composite = createComponent(function (props) {
  var htmlProps = useComposite(props);
  return createElement("div", htmlProps);
});
export { Composite, useComposite };